import React, { useState, useRef, useEffect, Suspense, lazy } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import assistantAvatar from "../../Assets/hero-portrait.png";
import WorkspaceScene from "./WorkspaceScene";
import DigitalTwinAvatar from "./DigitalTwinAvatar";
import AvatarStage from "../../avatar/AvatarStage";
import useAvatarState from "../../avatar/useAvatarState";
import Waveform from "./Waveform";
import useParallax from "../../hooks/useParallax";
import useSpeechAmplitude from "../../hooks/useSpeechAmplitude";
import RadialWaveform from "../../audio/RadialWaveform";
import {
  BsArrowRight,
  BsGraphUp,
  BsCheckCircleFill,
  BsGearFill,
  BsDiagram3Fill,
  BsSend,
  BsMicFill,
  BsStopFill,
  BsVolumeUpFill,
  BsVolumeMuteFill,
  BsThreeDotsVertical,
} from "react-icons/bs";
import { AiFillCloud } from "react-icons/ai";
import { GiBrain } from "react-icons/gi";
import { BookOpen, Layers3, MessagesSquare, Rocket, ShieldCheck } from "lucide-react";

// Phase 8: both are the heavy additions from Phases 6/7 (three.js/R3F/drei,
// and react-markdown+syntax-highlighter+mermaid respectively) — lazy so
// neither weighs down the initial hero bundle. AmbientParticles is pure
// decoration (null fallback is invisible, not a layout shift); assistant
// replies only render after a real API round-trip anyway, so the chunk
// has time to load in the background.
const AmbientParticles = lazy(() => import("./AmbientParticles"));
const AssistantMessage = lazy(() => import("./AssistantMessage"));

const AVATAR_RENDER_ENABLED = true;

const TAGS = [
  "AI Platforms",
  "MCP",
  "LLM Infrastructure",
  "Identity & Security",
  "Backend Systems",
  "Cloud & DevOps",
  "Distributed Infrastructure",
];

// A flat, unchanging "Thinking…" bubble for the entire wait reads as
// stalled/broken rather than as an AI actively working — especially once
// a reply takes more than a couple seconds. useAvatarState already
// alternates the avatar itself between "thinking" and "reasoning" every
// THINKING_PHASE_MS while `loading` is true (see useAvatarState.js); this
// mirrors that same alternation into the chat bubble's text instead of
// introducing a second, unsynced timer, so the two stay visually in step.
const THINKING_STATUS_LABELS = {
  thinking: "Searching memory…",
  reasoning: "Reasoning it through…",
};

const ASSISTANT_SUGGESTIONS = [
  { icon: <BsCheckCircleFill />, label: "What kind of work fits your background?" },
  { icon: <Layers3 />, label: "Tell me about your leadership experience" },
  { icon: <BsGearFill />, label: "What's your core technical expertise?" },
  { icon: <Rocket />, label: "What's your most impactful project?" },
  { icon: <BsSend />, label: "Can you email me your resume?" },
];

const TOP_STATS = [
  { icon: <Rocket />, color: "#e0452a", num: "5+", lab: "Years Experience" },
  { icon: <MessagesSquare />, color: "#ff7a5c", num: "15+", lab: "Repositories Shipped" },
  { icon: <Layers3 />, color: "#4caf50", num: "MCP", lab: "Agent Frameworks" },
  { icon: <BookOpen />, color: "#e0a02a", num: "4", lab: "Publications" },
  { icon: <ShieldCheck />, color: "#4fa8e0", num: "SOC 2", lab: "Security Controls" },
];

// Colors reference src/ui/tokens.css's categorical hue map (imported
// globally in App.js) instead of duplicating hex values here — one
// source of truth for "what color means what domain" across the site.
const BUILD_DELIVER = [
  { icon: <GiBrain />, color: "var(--h-ai)", title: "AI Platform Engineering", desc: "Agentic AI, MCP, A2A, multi-model orchestration, RAG, tools & integrations" },
  { icon: <BsDiagram3Fill />, color: "var(--h-infra)", title: "LLM Infrastructure", desc: "Self-hosted vLLM, Qwen3-32B, DeepSeek-R1, Bedrock fallback, scaling & cost optimization" },
  { icon: <BsCheckCircleFill />, color: "var(--h-security)", title: "Identity & Security", desc: "Multi-tenant IdP, SAML 2.0, OIDC, OAuth2, RBAC, MFA, zero-trust security" },
  { icon: <BsGearFill />, color: "var(--h-backend)", title: "Backend & APIs", desc: "High-scale APIs, event-driven systems, auth, payments, webhooks & real-time" },
  { icon: <BsGraphUp />, color: "var(--h-data)", title: "Data & Intelligence", desc: "Pipelines, vector stores, analytics, forecasting, anomaly detection" },
  { icon: <AiFillCloud />, color: "var(--h-cloud)", title: "Cloud & DevOps", desc: "AWS/GCP/Azure, K8s, CI/CD, IaC, monitoring & reliability" },
];

function localReply(question) {
  const query = question.toLowerCase();
  if (query.includes("fit") || query.includes("role")) {
    return "I bring 5+ years building production AI platforms \u2014 agentic AI/MCP, self-hosted LLM inference, identity security, and distributed systems \u2014 plus technical leadership of an engineering team with formal performance reviews. Ask about a specific area and I can go deeper.";
  }
  if (query.includes("leadership") || query.includes("lead")) {
    return "At EngineersMind I lead a team of engineers: I assign work, review code, run performance and probation reviews, and own release management and engineering standards.";
  }
  if (query.includes("technical") || query.includes("expertise") || query.includes("stack")) {
    return "My core stack is Python, Go, and TypeScript, with deep experience in self-hosted LLM serving (vLLM, Qwen3, DeepSeek-R1), agentic AI (MCP, A2A, LangGraph), identity/auth systems built from scratch, and cloud-native infrastructure on AWS/Kubernetes.";
  }
  if (query.includes("project") || query.includes("impact")) {
    return "One I'm proud of is a multi-tenant identity provider in Go/Echo that replaced Auth0, including an AI agent security phase: agent registration, machine-to-machine auth and per-agent audit trails, so agents are identifiable and auditable like human users.";
  }
  if (query.includes("visa") || query.includes("sponsor")) {
    return "For work-authorization questions, please email me directly and I'll answer personally.";
  }
  return "Thanks for asking. I build secure, production-ready AI platforms spanning LLM infrastructure, orchestration, identity, APIs, and cloud reliability. Ask about my fit for the role, leadership experience, technical expertise, or sponsorship needs.";
}

// Voice names/availability vary wildly by OS and browser, so instead of
// matching exact names we score every available voice and pick the best —
// this actually finds a good match on whatever platform the visitor is on.
const GOOD_VOICE_PATTERNS = [
  { re: /online \(natural\)/i, score: 100 },
  { re: /neural/i, score: 95 },
  { re: /\bnatural\b/i, score: 90 },
  { re: /premium|enhanced/i, score: 85 },
  { re: /google/i, score: 75 },
  { re: /\b(guy|ryan|daniel|alex|christopher|andrew|brian)\b/i, score: 60 },
];
const BAD_VOICE_PATTERNS = [/desktop/i, /espeak/i, /compact/i, /\bzira\b/i, /\bdavid\b/i];

// The assistant's persona is a single, consistent male voice — pick from
// these first regardless of which platform/browser is rendering, so the
// "who's speaking" identity doesn't shift between visitors. Extend this
// list as more platform voice names are found to sound off.
const MALE_VOICE_RE = /\b(guy|ryan|daniel|alex|david|christopher|eric|andrew|brian|roger|steffan|thomas|oliver|diego|fred|george|james|kevin|liam|matthew|will|mark|paul|tom)\b/i;
const FEMALE_VOICE_RE = /\b(samantha|karen|moira|tessa|serena|aria|jenny|zira|susan|victoria|fiona|kate|allison|ava|michelle|nicky|catherine|hazel|sonia|libby|emma|olivia|sara|zoe|amy|joanna|salli|kimberly|female)\b/i;

// Fixed delivery — applied to whichever underlying voice engine the
// browser provides, so the pacing/tone stays the same "measured,
// professional" persona everywhere even though the raw voice timbre
// still varies by platform (the Web Speech API gives no way around that).
const VOICE_PERSONA = { rate: 0.95, pitch: 0.9, volume: 1 };

function scoreVoice(voice) {
  let score = 0;
  for (const { re, score: s } of GOOD_VOICE_PATTERNS) {
    if (re.test(voice.name)) score = Math.max(score, s);
  }
  for (const re of BAD_VOICE_PATTERNS) {
    if (re.test(voice.name)) score -= 40;
  }
  if (voice.lang === "en-US") score += 8;
  else if (voice.lang?.startsWith("en")) score += 4;
  if (!voice.localService) score += 5;
  return score;
}

function pickPreferredVoice(voices) {
  if (!voices.length) return null;
  const englishVoices = voices.filter((v) => v.lang?.startsWith("en"));
  const pool = englishVoices.length ? englishVoices : voices;

  // Gender consistency first, voice quality second: prefer an explicitly
  // male-named voice; failing that, at least avoid an explicitly
  // female-named one; only fall back to the unfiltered pool if that
  // leaves nothing to speak with at all.
  const male = pool.filter((v) => MALE_VOICE_RE.test(v.name) && !FEMALE_VOICE_RE.test(v.name));
  const notFemale = pool.filter((v) => !FEMALE_VOICE_RE.test(v.name));
  const finalPool = male.length ? male : notFemale.length ? notFemale : pool;

  return finalPool.reduce((best, v) => (scoreVoice(v) > scoreVoice(best) ? v : best), finalPool[0]);
}

function waitForVoices(synth, timeoutMs = 1000) {
  return new Promise((resolve) => {
    const existing = synth.getVoices();
    if (existing.length) {
      resolve(existing);
      return;
    }
    const onChange = () => {
      synth.removeEventListener("voiceschanged", onChange);
      resolve(synth.getVoices());
    };
    synth.addEventListener("voiceschanged", onChange);
    setTimeout(() => {
      synth.removeEventListener("voiceschanged", onChange);
      resolve(synth.getVoices());
    }, timeoutMs);
  });
}

// Real lip-synced avatar generation (see lipsync-start.mjs/lipsync-status.mjs)
// takes 10-60s+, well past what a single Netlify Function call can do
// synchronously — the client starts a Replicate prediction, then polls this
// status endpoint until it's ready or the deadline below is hit.
const LIPSYNC_MAX_CHARS = 300; // mirrors lipsync-start.mjs's own cap
const LIPSYNC_POLL_INTERVAL_MS = 2000;
const LIPSYNC_POLL_TIMEOUT_MS = 8000; // per-poll network timeout, not the overall deadline
// The assistant's text answer is already fully visible (added to `messages`
// before speakWithLipSync is even called) well before any lip-synced clip
// could realistically finish — so this is not "how long can generation
// run", it's "how long is a visitor willing to watch the avatar's face
// keep pantomiming thinking after it already gave its answer in text".
// SadTalker generations that don't land inside this window still finish
// server-side eventually, but by then the moment has passed — better to
// drop to the canned speaking clip + real TTS voice (near-instant) than
// leave the avatar visibly stuck mid-thought for up to a minute.
const LIPSYNC_MAX_WAIT_MS = 12000;

// Thrown only for a real, terminal "generation failed" from the server —
// distinguishes that from a transient poll hiccup (network blip, single
// slow request) so pollLipSyncStatus knows which ones to keep retrying
// through vs. bail out of immediately.
class LipSyncFailedError extends Error {}

async function pollLipSyncStatus(jobId) {
  const deadline = Date.now() + LIPSYNC_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LIPSYNC_POLL_TIMEOUT_MS);
    try {
      const res = await fetch(`/.netlify/functions/lipsync-status?id=${encodeURIComponent(jobId)}`, {
        signal: controller.signal,
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === "ready") return data.videoUrl;
        if (data.status === "failed") throw new LipSyncFailedError(data.error || "lipsync generation failed");
      }
    } catch (err) {
      if (err instanceof LipSyncFailedError) throw err;
      // transient poll error — keep trying until the overall deadline
    } finally {
      clearTimeout(timeoutId);
    }
    await new Promise((resolve) => setTimeout(resolve, LIPSYNC_POLL_INTERVAL_MS));
  }
  throw new Error("lipsync generation timed out");
}

// A valid, minimal, genuinely-silent WAV (44-byte header, zero sample
// frames) — used only to "arm" the shared <audio> element below with a
// real playback attempt. `muted=true` autoplay would only prove the
// browser allows silent playback, not the real (unmuted) TTS/voice
// playback this element goes on to do every other turn.
const SILENT_WAV_SRC =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";

// Entrance animation for the hero's top-level panels — staggered by
// `custom` (a small index-based delay) so the page feels assembled
// rather than static on load. Distance/duration kept subtle per GOAL.md's
// "ambient motion... slowly" guidance, not a flashy reveal.
const panelVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.08 * i, ease: [0.2, 0.8, 0.2, 1] },
  }),
};

function HeroGlass() {
  const reduceMotion = useReducedMotion();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [listenSeconds, setListenSeconds] = useState(0);
  const [voiceOn, setVoiceOn] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [revealDone, setRevealDone] = useState({});
  const bottomRef = useRef(null);
  const recognitionRef = useRef(null);
  const menuRef = useRef(null);
  const voicesRef = useRef([]);
  const audioRef = useRef(null);
  const utteranceRef = useRef(null);
  const keepAliveRef = useRef(null);
  const photoSlotRef = useRef(null);
  // One <audio> element, created and played (silently) exactly once, right
  // inside the first real user gesture — see unlockMobileAudio() below for
  // why this exists and why speak() reuses this same element on every turn
  // instead of `new Audio(url)`.
  const sharedAudioElRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const { attach: attachSpeechAnalyser, getLevel: getSpeechLevel, primeContext: primeAudioContext } = useSpeechAmplitude();
  const [justCompleted, setJustCompleted] = useState(false);
  const wasLoadingForAvatarRef = useRef(false);
  const [lipSyncPending, setLipSyncPending] = useState(false);
  const [generatedClip, setGeneratedClip] = useState(null);
  // True only while the real TTS <audio> element (the one useSpeechAmplitude
  // is actually attached to) is driving playback — false during the
  // speakWithBrowserVoice() fallback, which has no audio element for the
  // analyser to read. VideoAvatar's amplitude-gated pause/resume must only
  // run when this is true; otherwise it reads permanent near-silence from a
  // stale/unattached analyser and freezes the speaking clip almost
  // immediately while the browser voice keeps talking underneath it.
  const [usingRealAudio, setUsingRealAudio] = useState(false);
  const lipSyncTokenRef = useRef(0);
  const pendingClipSettleRef = useRef(null);

  useParallax(photoSlotRef, { distance: 50, reduceMotion });

  // Brief "complete" pulse on the loading→!loading transition — the same
  // pattern DigitalTwinAvatar already uses internally for its own
  // completed state, lifted here so AvatarStage's video/poster renderer
  // (which doesn't have DigitalTwinAvatar's internals) gets the same signal.
  useEffect(() => {
    const wasLoading = wasLoadingForAvatarRef.current;
    wasLoadingForAvatarRef.current = loading;
    if (wasLoading && !loading) {
      setJustCompleted(true);
      const id = setTimeout(() => setJustCompleted(false), 1200);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [loading]);

  // lipSyncPending covers the generate+poll wait for a real lip-synced
  // clip — OR-ing it into `loading` extends the existing alternating
  // thinking/reasoning states over that whole window for free, with no
  // changes needed to useAvatarState.js itself.
  const avatarState = useAvatarState(
    { listening, loading: loading || lipSyncPending, speaking, completed: justCompleted },
    getSpeechLevel
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return undefined;
    function loadVoices() {
      voicesRef.current = synth.getVoices();
    }
    loadVoices();
    synth.addEventListener("voiceschanged", loadVoices);
    return () => synth.removeEventListener("voiceschanged", loadVoices);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onDocClick(e) {
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!listening) {
      setListenSeconds(0);
      return undefined;
    }
    setListenSeconds(0);
    const id = setInterval(() => setListenSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [listening]);

  useEffect(() => () => stopSpeaking(), []);

  // iOS Safari (and every other iOS browser — all WebKit under the hood)
  // only allows a *new* <audio>/AudioContext to start playing when the
  // play()/resume() call itself is synchronous inside a real user gesture.
  // Every path that speaks a reply (speak() / speakWithBrowserVoice()) only
  // runs after `ask()`'s `await fetch(ask-shreyash)` for the AI's answer —
  // by then the gesture is long gone, so on iOS the call silently does
  // nothing (desktop's far more permissive autoplay policy is why this was
  // never noticed there). ask() is always itself a direct click/submit
  // handler, so this runs synchronously at its top, once per page load, to
  // play a real (silent) clip through one persistent <audio> element and
  // resume the shared AudioContext right inside that gesture. Playback
  // permission then carries forward on that SAME element/context for every
  // later async-triggered call — see speak() below, which reuses
  // sharedAudioElRef instead of constructing a new Audio() per turn.
  function unlockMobileAudio() {
    if (audioUnlockedRef.current) return;
    audioUnlockedRef.current = true;
    primeAudioContext();
    const el = sharedAudioElRef.current || (sharedAudioElRef.current = new Audio());
    el.src = SILENT_WAV_SRC;
    el.play().catch(() => {});
    // Same problem, same fix, for the SpeechSynthesis fallback path: iOS
    // only allows speechSynthesis.speak() without a fresh gesture once one
    // utterance has already been spoken from inside a real one.
    const synth = window.speechSynthesis;
    if (synth) {
      const warmup = new SpeechSynthesisUtterance(" ");
      warmup.volume = 0;
      synth.speak(warmup);
    }
  }

  function stopSpeaking() {
    // Invalidate any in-flight lipsync generation/poll/playback so a new
    // question can't have an old attempt's fallback or clip land after it.
    lipSyncTokenRef.current += 1;
    if (pendingClipSettleRef.current) {
      pendingClipSettleRef.current();
      pendingClipSettleRef.current = null;
    }
    setLipSyncPending(false);
    setGeneratedClip(null);
    setUsingRealAudio(false);
    window.speechSynthesis?.cancel();
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
    utteranceRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setSpeaking(false);
  }

  // Fallback only: each browser exposes a different voice list, so this can
  // never guarantee the exact same audio everywhere — it's what runs if the
  // server-side voice (the one that IS consistent everywhere, see speak()
  // below) is unavailable.
  //
  // Two long-standing cross-browser SpeechSynthesis bugs this works around:
  // 1. Chromium silently cuts speech off after ~15s on longer utterances
  //    (chromium issue 679437) unless the synth is periodically nudged with
  //    pause()/resume() while it's speaking.
  // 2. The utterance can get garbage-collected mid-speech if nothing keeps
  //    a reference to it, silently stopping playback — so we hold one in a
  //    ref for the duration instead of only a local variable.
  async function speakWithBrowserVoice(text) {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (!voicesRef.current.length) {
      voicesRef.current = await waitForVoices(synth);
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;
    const voice = pickPreferredVoice(voicesRef.current);
    if (voice) utterance.voice = voice;
    utterance.rate = VOICE_PERSONA.rate;
    utterance.pitch = VOICE_PERSONA.pitch;
    utterance.volume = VOICE_PERSONA.volume;
    utterance.onstart = () => {
      setSpeaking(true);
      keepAliveRef.current = setInterval(() => {
        if (!synth.speaking) return;
        synth.pause();
        synth.resume();
      }, 10000);
    };
    const finish = () => {
      setSpeaking(false);
      if (keepAliveRef.current) {
        clearInterval(keepAliveRef.current);
        keepAliveRef.current = null;
      }
      utteranceRef.current = null;
    };
    utterance.onend = finish;
    utterance.onerror = finish;
    synth.speak(utterance);
  }

  async function speak(text) {
    if (!voiceOn) return;
    stopSpeaking();
    try {
      const res = await fetch("/.netlify/functions/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("tts unavailable");
      const blob = await res.blob();
      if (!blob.size) throw new Error("tts returned empty audio");
      const url = URL.createObjectURL(blob);
      // Reuse the one <audio> element unlockMobileAudio() already played
      // (silently) inside a real gesture — swapping its src and calling
      // play() again keeps that same iOS playback permission, whereas a
      // fresh `new Audio(url)` here would be an entirely new, un-gestured
      // element and iOS would silently refuse to play it.
      const audio = sharedAudioElRef.current || (sharedAudioElRef.current = new Audio());
      audio.src = url;
      audioRef.current = audio;
      attachSpeechAnalyser(audio);
      let started = false;
      audio.onplay = () => {
        started = true;
        setSpeaking(true);
        setUsingRealAudio(true);
      };
      audio.onended = () => {
        setSpeaking(false);
        setUsingRealAudio(false);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setSpeaking(false);
        setUsingRealAudio(false);
        URL.revokeObjectURL(url);
      };
      await audio.play();
      // audio.play() resolving only means the browser accepted the
      // request, not that the `onplay` event (which drives the avatar's
      // speaking state) actually fired — if it hasn't shortly after,
      // treat it as a silent failure and fall back to the browser voice
      // instead of leaving the avatar stuck on "Idle" through a real
      // spoken reply.
      await new Promise((resolve) => setTimeout(resolve, 300));
      if (!started && audioRef.current === audio) {
        throw new Error("playback did not start");
      }
    } catch {
      speakWithBrowserVoice(text);
    }
  }

  // Plays a Replicate-generated, real lip-synced clip as the sole media
  // element for this turn (video + audio are the same file — SadTalker
  // muxes them together — so there's no separate <audio> element and no
  // drift risk between two independently-buffered elements). Resolves once
  // the clip finishes; rejects if it fails to load/play, which
  // speakWithLipSync treats the same as any other lipsync failure: fall
  // back to the canned clip + TTS audio via speak().
  function playGeneratedClip(videoUrl, token) {
    return new Promise((resolve, reject) => {
      pendingClipSettleRef.current = () => reject(new Error("interrupted"));
      setSpeaking(true);
      setGeneratedClip({
        url: videoUrl,
        onEnded: () => {
          if (lipSyncTokenRef.current !== token) return;
          pendingClipSettleRef.current = null;
          setSpeaking(false);
          setGeneratedClip(null);
          resolve();
        },
        onError: () => {
          if (lipSyncTokenRef.current !== token) return;
          pendingClipSettleRef.current = null;
          setSpeaking(false);
          setGeneratedClip(null);
          reject(new Error("generated clip failed to play"));
        },
      });
    });
  }

  // Tries to generate and play a real lip-synced clip for this turn;
  // any failure at any stage (unconfigured, over the daily cap, generation
  // failed, timed out, or the clip itself failed to load) falls straight
  // through to the existing speak() path — canned speaking.mp4 + TTS audio
  // — so voice output always works even when lip sync doesn't.
  async function speakWithLipSync(text) {
    if (!voiceOn) return;
    stopSpeaking();
    if (text.length > LIPSYNC_MAX_CHARS) {
      speak(text);
      return;
    }
    const token = ++lipSyncTokenRef.current;
    setLipSyncPending(true);
    try {
      const startRes = await fetch("/.netlify/functions/lipsync-start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!startRes.ok) throw new Error("lipsync unavailable");
      const { jobId } = await startRes.json();
      if (!jobId) throw new Error("lipsync unavailable");
      const videoUrl = await pollLipSyncStatus(jobId);
      if (lipSyncTokenRef.current !== token) return;
      await playGeneratedClip(videoUrl, token);
    } catch {
      if (lipSyncTokenRef.current === token) speak(text);
    } finally {
      if (lipSyncTokenRef.current === token) setLipSyncPending(false);
    }
  }

  function toggleVoiceOutput() {
    setVoiceOn((v) => {
      const next = !v;
      if (!next) stopSpeaking();
      return next;
    });
  }

  async function ask(question) {
    if (!question.trim() || loading) return;
    unlockMobileAudio();
    stopSpeaking();
    setMessages((m) => [...m, { role: "user", content: question }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch("/.netlify/functions/ask-shreyash", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          history: messages.slice(-6).map(({ role, content }) => ({ role, content })),
        }),
      });
      if (!res.ok) throw new Error("Assistant unavailable");
      const data = await res.json();
      const answer = data.answer || localReply(question);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: answer, resumeProposal: data.resumeProposal || null },
      ]);
      speakWithLipSync(answer);
    } catch {
      // Generic canned text, not a real personalized answer — spending up
      // to LIPSYNC_MAX_WAIT_MS pantomiming "thinking" over a network round
      // trip to Replicate for this is wasted budget and wasted time; go
      // straight to the fast canned-clip + TTS path instead.
      const answer = localReply(question);
      setMessages((m) => [...m, { role: "assistant", content: answer, offline: true }]);
      speak(answer);
    } finally {
      setLoading(false);
    }
  }

  function updateMessage(index, patch) {
    setMessages((m) => m.map((msg, i) => (i === index ? { ...msg, ...patch } : msg)));
  }

  function confirmSendResume(index, proposal) {
    updateMessage(index, { resumeSendStatus: "sending" });
    fetch("/.netlify/functions/send-resume", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: proposal.email, variant: proposal.variant, message: proposal.message }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to send the resume.");
        updateMessage(index, { resumeSendStatus: "sent" });
      })
      .catch((err) => updateMessage(index, { resumeSendStatus: "error", resumeSendError: err.message }));
  }

  function cancelSendResume(index) {
    updateMessage(index, { resumeSendStatus: "cancelled" });
  }

  function toggleVoiceInput() {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setMessages((m) => [...m, { role: "assistant", content: "Voice input is not supported in this browser. Please type your question instead." }]);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.onresult = (event) => setInput(event.results[0][0].transcript);
    recognitionRef.current = recognition;
    recognition.start();
  }

  return (
    <div className="hg-hero">
      <Suspense fallback={null}>
        <AmbientParticles reduceMotion={reduceMotion} />
      </Suspense>
      <div className="hg-topbar">
        <div className="hg-topstats">
          {TOP_STATS.map((s) => (
            <div className="hg-topstat" key={s.lab}>
              <span className="hg-topstat-icon" style={{ color: s.color }}>
                {s.icon}
              </span>
              <div>
                <div className="hg-topstat-num" style={{ color: s.color }}>
                  {s.num}
                </div>
                <div className="hg-topstat-lab">{s.lab}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="hg-grid">
        <motion.div
          className="hg-left"
          variants={panelVariants}
          initial={reduceMotion ? false : "hidden"}
          animate="visible"
          custom={0}
        >
          <h1 className="hg-name">
            Shreyash
            <br />
            <span>Gondane</span>
          </h1>
          <p className="hg-title">Software Engineer, AI Platform Engineering — EngineersMind</p>
          <p className="hg-desc">
            I design, build and scale production AI platforms:
            LLM inference, retrieval, MCP agent frameworks, and secure identity
            for AI agents.
          </p>
          <div className="hg-tags">
            {TAGS.map((t) => (
              <span className="hg-tag" key={t}>
                {t}
              </span>
            ))}
          </div>
          <div className="hg-cta-row">
            <Link className="hero-btn" to="/project">
              View My Work <BsArrowRight />
            </Link>
            <Link className="hg-btn-outline" to="/resume">
              Download Resume
            </Link>
          </div>
        </motion.div>

        <motion.div
          className="hg-right"
          variants={panelVariants}
          initial={reduceMotion ? false : "hidden"}
          animate="visible"
          custom={2}
        >
          <div className="hg-panel hg-glass">
            <div className="hg-panel-header">
              <img src={assistantAvatar} alt="" className="hg-avatar" />
              Shreyash AI Assistant
              <div className="hg-header-actions">
                <button
                  type="button"
                  className="hg-icon-btn"
                  aria-label={voiceOn ? "Mute voice replies" : "Unmute voice replies"}
                  aria-pressed={voiceOn}
                  onClick={toggleVoiceOutput}
                >
                  {voiceOn ? <BsVolumeUpFill /> : <BsVolumeMuteFill />}
                </button>
                <div className="hg-menu" ref={menuRef}>
                  <button
                    type="button"
                    className="hg-icon-btn"
                    aria-label="More options"
                    aria-haspopup="true"
                    aria-expanded={menuOpen}
                    onClick={() => setMenuOpen((o) => !o)}
                  >
                    <BsThreeDotsVertical />
                  </button>
                  {menuOpen && (
                    <div className="hg-menu-dropdown">
                      <button
                        type="button"
                        onClick={() => {
                          stopSpeaking();
                          setMessages([]);
                          setMenuOpen(false);
                        }}
                      >
                        Clear conversation
                      </button>
                    </div>
                  )}
                </div>
                <span className="hd-live-dot" /> Online
              </div>
            </div>
            <div className="hg-panel-body">
              <div className="hg-chat-scroll" aria-live="polite" aria-busy={loading}>
                {messages.length === 0 &&
                  <div className="hg-greeting">Hi, I’m Shreyash’s AI assistant. Ask the questions that matter most for screening — fit, leadership, technical depth, impact, or logistics.</div>}
                {messages.map((m, i) => (
                  <div key={i} className={`hg-msg hg-msg-${m.role}`}>
                    {m.role === "assistant" ? (
                      <Suspense fallback={m.content}>
                        <AssistantMessage
                          content={m.content}
                          reduceMotion={reduceMotion}
                          onRevealDone={() => setRevealDone((prev) => (prev[i] ? prev : { ...prev, [i]: true }))}
                        />
                      </Suspense>
                    ) : (
                      m.content
                    )}
                    {m.role === "assistant" && i === messages.length - 1 && speaking && (
                      <Waveform active bars={14} />
                    )}
                    {m.resumeProposal && revealDone[i] && (
                      <div className="hg-resume-confirm">
                        {!m.resumeSendStatus && (
                          <>
                            <p className="hg-resume-confirm-text">
                              Send my resume ({m.resumeProposal.variant}) to {m.resumeProposal.email}?
                            </p>
                            <div className="hg-resume-confirm-actions">
                              <button
                                type="button"
                                className="hg-resume-confirm-send"
                                onClick={() => confirmSendResume(i, m.resumeProposal)}
                              >
                                Send
                              </button>
                              <button
                                type="button"
                                className="hg-resume-confirm-cancel"
                                onClick={() => cancelSendResume(i)}
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        )}
                        {m.resumeSendStatus === "sending" && <p className="hg-resume-confirm-text">Sending…</p>}
                        {m.resumeSendStatus === "sent" && (
                          <p className="hg-resume-confirm-text">Sent to {m.resumeProposal.email}.</p>
                        )}
                        {m.resumeSendStatus === "error" && (
                          <p className="hg-resume-confirm-text hg-resume-confirm-error">{m.resumeSendError}</p>
                        )}
                        {m.resumeSendStatus === "cancelled" && <p className="hg-resume-confirm-text">Cancelled.</p>}
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="hg-msg hg-msg-assistant hg-typing">
                    {THINKING_STATUS_LABELS[avatarState] || "Thinking…"}
                  </div>
                )}
                <div className={`hg-suggestions ${messages.length === 0 ? "hg-suggestions-first" : ""}`}>
                  {ASSISTANT_SUGGESTIONS.map((s) => (
                    <button
                      className="hg-suggestion"
                      key={s.label}
                      onClick={() => ask(s.label)}
                      type="button"
                      disabled={loading}
                    >
                      <span className="hg-suggestion-icon">{s.icon}</span>
                      {s.label}
                    </button>
                  ))}
                </div>
                <div ref={bottomRef} />
              </div>
              <form
                className="hg-ask-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  ask(input);
                }}
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything…"
                  disabled={loading}
                />
                <button
                  className={`hg-mic-button ${listening ? "hg-mic-button-active" : ""}`}
                  type="button"
                  aria-label={listening ? "Stop voice input" : "Start voice input"}
                  aria-pressed={listening}
                  onClick={toggleVoiceInput}
                >
                  {listening ? <BsStopFill /> : <BsMicFill />}
                </button>
                <button type="submit" aria-label="Send" disabled={loading || !input.trim()}>
                  <BsSend />
                </button>
              </form>
              {listening && (
                <div className="hg-listening-bar" role="status">
                  {/* Mic input has no accessible amplitude (SpeechRecognition
                      doesn't expose audio levels) — the ring's own per-bar
                      seeded jitter still gives it visible life at a fixed
                      target rather than looking static. */}
                  <RadialWaveform
                    active
                    amplitude={0.5}
                    label="Listening…"
                    elapsedSeconds={listenSeconds}
                    onMicClick={toggleVoiceInput}
                    reduceMotion={reduceMotion}
                  />
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="hg-center"
          variants={panelVariants}
          initial={reduceMotion ? false : "hidden"}
          animate="visible"
          custom={1}
        >
          {/* Separate wrapper for the GSAP scroll-parallax ref (Phase 5) —
              .hg-photo-slot already owns its own CSS transform animation
              (photo-float) and sits inside a Framer Motion node, so the
              scrubbed GSAP transform goes on its own plain, unanimated
              element instead of fighting either of those for `transform`. */}
          <div className="hg-parallax-wrap" ref={photoSlotRef}>
            <div className="hg-photo-slot">
              <WorkspaceScene />
              {AVATAR_RENDER_ENABLED && (
                <AvatarStage
                  state={avatarState}
                  reduceMotion={reduceMotion}
                  getSpeechLevel={usingRealAudio ? getSpeechLevel : undefined}
                  generatedClip={generatedClip}
                  fallback={
                    <DigitalTwinAvatar
                      listening={listening}
                      loading={loading}
                      speaking={speaking}
                      reduceMotion={reduceMotion}
                      getSpeechLevel={getSpeechLevel}
                    />
                  }
                />
              )}
              {!reduceMotion && (
                <motion.div
                  className="hg-photo-sweep"
                  aria-hidden="true"
                  animate={{ x: ["-120%", "220%"] }}
                  transition={{ duration: 5, repeat: Infinity, repeatDelay: 3, ease: "easeInOut" }}
                />
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          className="hg-panel hg-glass hg-build-panel"
          variants={panelVariants}
          initial={reduceMotion ? false : "hidden"}
          animate="visible"
          custom={3}
        >
          <div className="hg-panel-header">
            What I Build &amp; Deliver
            <Link to="/project" className="hg-view-all">
              View All <BsArrowRight />
            </Link>
          </div>
          <div className="hg-build-grid hg-build-grid-wide">
            {BUILD_DELIVER.map((b) => (
              <div className="hg-build-item" key={b.title} style={{ "--accent": b.color }}>
                <span
                  className="hg-build-icon"
                  style={{ background: `color-mix(in srgb, ${b.color} 13%, transparent)`, color: b.color }}
                >
                  {b.icon}
                </span>
                <div className="hg-build-title">{b.title}</div>
                <div className="hg-build-desc">{b.desc}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default HeroGlass;
